import { useEffect, useMemo, useRef, useState } from 'react';
import {
  api,
  applyCallResult,
  errorCode,
  isOffline,
  UnauthenticatedError,
  type LaneRow,
  type LiveAssignment,
  type RangeSession,
} from '../lib/api';
import { disciplineLabel, isTrainingNotStarted } from '@flm9/i18n/labels';
import { getLocalDb } from '@flm9/sync';
import { errorText } from '../lib/errorText';
import { getLastRosterCall, setLastRosterCall } from '../lib/rosterCall';
import { formatMagazines } from '../lib/magazines';
import { SignInScreen } from './SignInScreen';

interface Props {
  /** Switch to a session list (back button on the roster). */
  onPickSession: () => void;
  /** Switch to drill picker — start a new stage. */
  onPickDrill: () => void;
  /** Switch to scenario picker — append a scenario's stages to the session. */
  onPickScenario: () => void;
  /** Close the running session ("Badeschluss"). */
  onBadeschluss: () => void;
  /** Open the score screen for a specific assignment (already claimed). */
  onScore: (assignment: LiveAssignment, roster: LiveAssignment[]) => void;
  session: RangeSession;
  /** Browser online state (App banner) — "Training starten" needs the network. */
  online: boolean;
  /** Fresh session row (status changed: started here or by the first round). */
  onSessionChange: (session: RangeSession) => void;
}

/** Roles holding `session.start` (permissions matrix: admin, range_officer). */
const STARTER_ROLES = new Set(['admin', 'range_officer']);

interface StageGroup {
  stage: LiveAssignment['stage'];
  schemaCode: LiveAssignment['scoringSchemaCode'];
  scenarioCode: string | null;
  scenarioTitleI18n: Record<string, string> | null;
  disciplineCode: string | null;
  rounds: number[];
  current: number;
  rows: LiveAssignment[];
}

const LOCALE = 'de-AT';
function pickTitle(t: Record<string, string> | null | undefined): string | null {
  if (!t) return null;
  return t[LOCALE] ?? t['en'] ?? Object.values(t)[0] ?? null;
}

function shooterLabel(a: LiveAssignment): string {
  return `${a.memberLastName ?? 'Gast'}, ${a.memberFirstName ?? ''}`.trim().replace(/, $/, '');
}

/** How long "{Name} aufgerufen – Rückgängig" stays on the roster (REF-18). */
const UNDO_MS = 10_000;

/** Still undoable: called by me and not begun (server re-checks). */
function undoable(a: LiveAssignment): boolean {
  return (
    a.ownedByMe &&
    a.assignment.calledAt != null &&
    a.shotCount === 0 &&
    a.assignment.standbyAt == null &&
    a.assignment.completedAt == null &&
    a.assignment.disqualifiedAt == null
  );
}

export function RosterScreen({
  session,
  onPickSession,
  onPickDrill,
  onPickScenario,
  onBadeschluss,
  onScore,
  online,
  onSessionChange,
}: Props) {
  const [assignments, setAssignments] = useState<LiveAssignment[]>([]);
  // Load error (cleared by the next successful poll) vs. error of a tap
  // (call / shuffle / undo — stays until "Erneut versuchen").
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [needsAuth, setNeedsAuth] = useState(false);
  // REF-18: undo bar for the call made from this roster.
  // The call itself lives in lib/rosterCall.ts (the Score screen offers it too).
  const [undo, setUndo] = useState(() => getLastRosterCall());
  // Offline (REF-3): keep the last list and say how old it is instead of
  // replacing it with an error.
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [offline, setOffline] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  // stageId → selected roundNo (so multi-stage sessions remember per-stage choice)
  const [pickedRound, setPickedRound] = useState<Record<string, number>>({});
  // Lanes available at this session's location. Empty array = single-
  // lane (server-side: still one row, just the seeded "Bahn 1"), in
  // which case we hide the picker UI entirely.
  const [lanes, setLanes] = useState<LaneRow[]>([]);
  // Inline lane prompt — opens when the referee taps an unowned row on
  // a multi-lane location without a pre-assigned lane (or when the
  // server rejects the claim with `lane_required`).
  const [lanePrompt, setLanePrompt] = useState<LiveAssignment | null>(null);
  const multiLane = lanes.length > 1;
  // "Training starten" (draft/scheduled only). null = roles unknown (call
  // failed) → show the button; the server re-checks and answers in German.
  const [roles, setRoles] = useState<string[] | null>(null);
  const [starting, setStarting] = useState(false);

  // Current status for the 5 s poll below (its closure is from the first render).
  const statusRef = useRef(session.status);
  statusRef.current = session.status;

  // Fresh status on open: the first round start sets a draft training live,
  // and the session passed in is the picker's snapshot.
  const reloadSession = () =>
    api
      .listSessions()
      .then((rows) => {
        const fresh = rows.find((r) => r.id === session.id);
        if (fresh && fresh.status !== statusRef.current) onSessionChange(fresh);
      })
      .catch(() => {});

  useEffect(() => {
    void reloadSession();
    let cancelled = false;
    api
      .myRoles()
      .then((r) => {
        if (!cancelled) setRoles(r);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  const refresh = () => {
    setNeedsAuth(false);
    // The error stays until a poll succeeds, so the banner doesn't flicker
    // with every 5 s retry.
    api
      .liveAssignments(session.id)
      .then((rows) => {
        setError(null);
        setAssignments(rows);
        setLoadedAt(new Date());
        setOffline(false);
        // Another referee's first round sets the training live — keep
        // polling the status until it is, so the hint goes and Badeschluss
        // appears without reopening the roster.
        if (isTrainingNotStarted(statusRef.current)) void reloadSession();
      })
      .catch((err) => {
        if (err instanceof UnauthenticatedError) setNeedsAuth(true);
        else if (isOffline(err)) setOffline(true);
        else setError(errorText(err));
      });
  };

  // Offer it once: consumed on mount (not in the state initializer, which
  // StrictMode runs twice).
  useEffect(() => {
    setLastRosterCall(null);
  }, []);

  useEffect(() => {
    if (!undo) return;
    const t = window.setTimeout(() => setUndo(null), UNDO_MS);
    return () => window.clearTimeout(t);
  }, [undo]);

  useEffect(() => {
    refresh();
    const t = window.setInterval(refresh, 5_000);
    return () => window.clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.id]);

  // Lanes for this session's location. Fetched once per session change
  // (lanes don't change mid-training in practice) so the claim flow
  // doesn't pay a roundtrip every tap.
  useEffect(() => {
    if (!session.locationId) {
      setLanes([]);
      return;
    }
    let cancelled = false;
    api
      .lanesForLocation(session.locationId)
      .then((rows) => {
        if (!cancelled) setLanes(rows);
      })
      .catch(() => {
        if (!cancelled) setLanes([]);
      });
    return () => {
      cancelled = true;
    };
  }, [session.locationId]);

  const groups = useMemo<StageGroup[]>(() => {
    const byStage = new Map<string, StageGroup>();
    for (const a of assignments) {
      let g = byStage.get(a.stage.id);
      if (!g) {
        g = {
          stage: a.stage,
          schemaCode: a.scoringSchemaCode,
          scenarioCode: a.scenarioCode,
          scenarioTitleI18n: a.scenarioTitleI18n,
          disciplineCode: a.disciplineCode,
          rounds: [],
          current: 1,
          rows: [],
        };
        byStage.set(a.stage.id, g);
      }
      if (!g.rounds.includes(a.round.roundNo)) g.rounds.push(a.round.roundNo);
    }
    for (const g of byStage.values()) {
      g.rounds.sort((a, b) => a - b);
      const picked = pickedRound[g.stage.id];
      g.current =
        picked != null && g.rounds.includes(picked) ? picked : (g.rounds[g.rounds.length - 1] ?? 1);
      // Keep the server's squad-number order so the displayed roster
      // matches how the referee actually advances through shooters
      // ("Weiter" walks the roster array via nextPending). The server
      // orders liveAssignments by stage.ord, roundNo, shootOrder (set by
      // "Mischen"), then squadNo.
      g.rows = assignments.filter(
        (a) => a.stage.id === g.stage.id && a.round.roundNo === g.current,
      );
    }
    return [...byStage.values()].sort((a, b) => a.stage.ord - b.stage.ord);
  }, [assignments, pickedRound]);

  if (needsAuth) {
    return <SignInScreen onSignedIn={refresh} />;
  }

  function retry() {
    setActionError(null);
    refresh();
  }

  const errorBox = (message: string) => (
    <div role="alert" className="space-y-3 rounded bg-red-900/30 p-3 text-sm text-red-300">
      <p>Fehler: {message}</p>
      <button
        type="button"
        onClick={retry}
        className="min-h-12 rounded-lg border border-red-400/60 px-4 py-2 font-semibold text-red-100 hover:bg-red-900/40"
      >
        Erneut versuchen
      </button>
    </div>
  );
  // Nothing loaded yet → the error is all there is to show. With a list
  // on screen it's a banner above it instead (REF-11).
  if (error && loadedAt == null) return errorBox(error);
  const bannerError = actionError ?? error;

  // Hide the undo once the row shows the shooter has begun (or isn't mine).
  const undoRow = undo ? assignments.find((a) => a.assignment.id === undo.assignmentId) : null;
  const showUndo = undo != null && (undoRow == null || undoable(undoRow));

  async function uncall(assignmentId: string) {
    setUndo(null);
    setBusy(`uncall:${assignmentId}`);
    try {
      await api.uncallShooter(assignmentId);
      refresh();
    } catch (err) {
      if (err instanceof UnauthenticatedError) setNeedsAuth(true);
      else if (isOffline(err)) setActionError('Offline — Rückgängig geht nur mit Verbindung.');
      else setActionError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function claimAndScore(a: LiveAssignment, laneId?: string | null) {
    setBusy(a.assignment.id);
    setActionError(null);
    try {
      // Calling = claiming in the 3-step flow (Aufrufen → Standby → Schüsse).
      // Re-opening a shooter who is already called or mid-run only
      // re-locks (legacy claim) so it doesn't reset their standby /
      // bump the "Aufgerufen" stamp on the boards.
      if (a.assignment.calledAt != null || a.shotCount > 0) {
        const res = await api.claim(a.assignment.id, laneId);
        onScore(
          {
            ...a,
            assignment: { ...a.assignment, lockedAt: res?.lockedAt ?? a.assignment.lockedAt },
          },
          assignments,
        );
      } else {
        const res = await api.callShooter(a.assignment.id, laneId);
        setLastRosterCall({
          assignmentId: a.assignment.id,
          label: shooterLabel(a),
          at: Date.now(),
        });
        onScore(applyCallResult(a, res), assignments);
      }
    } catch (err) {
      // Server forces a lane pick on multi-lane locations; open the
      // inline picker instead of surfacing a raw error.
      if (err instanceof UnauthenticatedError) {
        setNeedsAuth(true);
      } else if (isOffline(err)) {
        // A shooter this referee already holds keeps the server lock, so
        // scoring can go on and the shots sync later (REF-3) — as long as
        // this tablet has their shots (new ones would restart at #1).
        const cached = await getLocalDb()
          .shots.where('roundAssignmentId')
          .equals(a.assignment.id)
          .count();
        if (a.ownedByMe && cached >= a.shotCount) onScore(a, assignments);
        else if (a.ownedByMe)
          alert(
            'Offline — die bisherigen Schüsse dieses Schützen sind auf diesem Tablet nicht gespeichert. Bitte mit Verbindung öffnen.',
          );
        else
          alert(
            'Offline — neue Schützen bitte im Wertungsbildschirm mit „Aufrufen" holen; die Liste hier braucht Verbindung.',
          );
      } else if (errorCode(err) === 'lane_required') {
        setLanePrompt(a);
      } else {
        setActionError(errorText(err));
      }
    } finally {
      setBusy(null);
    }
  }

  // Shuffle the not-yet-started shooters of the shown round. The server
  // decides who is movable (see sessions.shuffleRoundOrder); the claimed
  // shooter is included as long as their timer hasn't started.
  async function shuffleRound(roundId: string) {
    if (!confirm('Reihenfolge der noch wartenden Schützen zufällig mischen?')) return;
    setBusy(`shuffle:${roundId}`);
    setActionError(null);
    try {
      await api.shuffleRoundOrder(roundId);
      refresh();
    } catch (err) {
      if (errorCode(err) === 'nothing_to_shuffle') alert('Weniger als zwei wartende Schützen.');
      else if (err instanceof UnauthenticatedError) setNeedsAuth(true);
      else setActionError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function startTraining() {
    if (
      !confirm(
        'Training jetzt starten? Es wird damit „live“ – Anzeigetafel und Badeschluss sind dann verfügbar.',
      )
    )
      return;
    setStarting(true);
    setActionError(null);
    try {
      await api.startSession(session.id);
      onSessionChange({ ...session, status: 'live' });
    } catch (err) {
      if (err instanceof UnauthenticatedError) setNeedsAuth(true);
      else if (errorCode(err) === 'session_not_draft') void reloadSession();
      else if (isOffline(err)) setActionError('Offline — Training starten geht nur mit Verbindung.');
      else setActionError(errorText(err));
    } finally {
      setStarting(false);
    }
  }

  const notStarted = isTrainingNotStarted(session.status);
  // Mirrors requirePermOrTrainingOwner('session.start'): officers, or the
  // owner of a self-signed training (a plain member's list holds only theirs).
  const canStart =
    roles == null ||
    roles.some((r) => STARTER_ROLES.has(r)) ||
    (session.kind === 'training' && session.signOffState === 'self_signed');
  const startOffline = !online || offline;

  // Tap handler that decides whether to prompt for a lane first. On
  // single-lane locations we go straight to claim. On multi-lane, if
  // the row already has a lane (admin pre-assigned or referee picked
  // earlier) we claim with that lane; otherwise prompt.
  function onRowTap(a: LiveAssignment) {
    if (!multiLane) {
      void claimAndScore(a);
      return;
    }
    const fallback = a.laneId ?? a.participationLaneId ?? null;
    if (fallback) void claimAndScore(a, fallback);
    else setLanePrompt(a);
  }

  return (
    <section className="space-y-4">
      <header className="flex items-center justify-between">
        <button onClick={onPickSession} className="min-h-12 pr-3 text-sm text-zinc-500 underline">
          ← anderes Training
        </button>
        <h2 className="truncate text-lg font-bold">{session.name}</h2>
      </header>

      {offline && (
        <p
          role="status"
          className="rounded border border-amber-700 bg-amber-950/40 px-3 py-2 text-sm text-amber-200"
        >
          Offline —{' '}
          {loadedAt
            ? `Stand ${loadedAt.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit' })}`
            : 'Liste noch nicht geladen'}
          . Schüsse werden gespeichert und später übertragen.
        </p>
      )}

      {bannerError && errorBox(bannerError)}

      {notStarted && (
        <div
          data-testid="training-not-started"
          className="space-y-3 rounded border border-zinc-700 bg-zinc-900/60 p-3"
        >
          <p className="text-sm text-zinc-300">
            Training ist noch nicht gestartet – startet automatisch mit der ersten Runde.
          </p>
          {canStart && (
            <>
              <button
                type="button"
                onClick={() => void startTraining()}
                disabled={starting || startOffline}
                className="min-h-12 w-full rounded-lg border border-emerald-600 px-4 py-3 text-base font-bold text-emerald-200 hover:bg-emerald-900/40 disabled:opacity-50"
              >
                {starting ? 'Starte…' : '▶ Training starten'}
              </button>
              {startOffline && (
                <p className="text-xs text-amber-300">Starten geht nur mit Verbindung.</p>
              )}
            </>
          )}
        </div>
      )}

      <div className="flex gap-2">
        <button
          onClick={onPickDrill}
          className="flex-1 rounded-lg bg-emerald-700 px-4 py-3 text-base font-bold text-white hover:bg-emerald-600 active:bg-emerald-800"
        >
          + Übung
        </button>
        <button
          onClick={onPickScenario}
          className="flex-1 rounded-lg border border-emerald-700 bg-emerald-900/40 px-4 py-3 text-base font-bold text-emerald-200 hover:border-emerald-500"
        >
          + Szenario
        </button>
      </div>

      {lanePrompt && (
        <LanePromptModal
          assignment={lanePrompt}
          lanes={lanes}
          onCancel={() => setLanePrompt(null)}
          onPick={(laneId) => {
            const a = lanePrompt;
            setLanePrompt(null);
            void claimAndScore(a, laneId);
          }}
        />
      )}

      {groups.length === 0 ? (
        <div className="rounded border border-dashed border-zinc-800 p-4 text-sm text-zinc-400">
          Noch keine Runden — auf „+ Übung" tippen, um die erste Runde zu erzeugen.
        </div>
      ) : (
        groups.map((g) => {
          return (
            <section
              key={g.stage.id}
              className="space-y-2 rounded border border-zinc-800 bg-zinc-900/40 p-3"
            >
              <div className="flex items-end justify-between gap-2">
                <div>
                  <p className="text-xs text-zinc-500">Stage {g.stage.ord}</p>
                  <h3 className="text-base font-bold">{g.stage.name}</h3>
                  <p className="text-xs text-zinc-500">
                    {g.stage.shotCount} Schuss
                    {formatMagazines(g.stage.magazines)
                      ? ` (${formatMagazines(g.stage.magazines)})`
                      : ''}{' '}
                    · {g.schemaCode}
                    {disciplineLabel(g.disciplineCode)
                      ? ` · ${disciplineLabel(g.disciplineCode)}`
                      : ''}
                  </p>
                  {g.scenarioCode && (
                    <p className="mt-1 inline-flex items-center gap-1 rounded bg-emerald-900/40 px-2 py-0.5 text-xs font-semibold text-emerald-300">
                      <span aria-hidden>◉</span>
                      Szenario: {pickTitle(g.scenarioTitleI18n) ?? g.scenarioCode}
                    </p>
                  )}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <label className="text-xs text-zinc-500">
                    Runde
                    <select
                      value={g.current}
                      onChange={(e) =>
                        setPickedRound((prev) => ({
                          ...prev,
                          [g.stage.id]: Number(e.target.value),
                        }))
                      }
                      className="ml-2 rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-sm text-zinc-100"
                    >
                      {g.rounds.map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                  </label>
                  {(() => {
                    const round = g.rows[0]?.round;
                    if (!round || round.endedAt != null) return null;
                    const waiting = g.rows.filter(
                      (a) =>
                        a.shotCount === 0 &&
                        a.assignment.standbyAt == null &&
                        a.assignment.completedAt == null &&
                        a.assignment.disqualifiedAt == null,
                    ).length;
                    return (
                      <button
                        onClick={() => void shuffleRound(round.id)}
                        disabled={busy !== null || waiting < 2}
                        className="min-h-12 rounded border border-zinc-700 px-3 py-1 text-xs font-semibold text-zinc-200 hover:border-amber-500 disabled:opacity-40"
                      >
                        🔀 Mischen
                      </button>
                    );
                  })()}
                </div>
              </div>

              <ul className="space-y-1">
                {g.rows.map((a) => {
                  const confirmed = a.assignment.completedAt != null;
                  const dq = a.assignment.disqualifiedAt != null;
                  const done = !confirmed && !dq && a.shotCount >= g.stage.shotCount;
                  const inProgress =
                    !confirmed && !dq && a.shotCount > 0 && a.shotCount < g.stage.shotCount;
                  const owned = a.ownedByMe;
                  const otherRef = !owned && !!a.assignment.assignedRefereeUserId;
                  return (
                    <li key={a.assignment.id}>
                      <button
                        onClick={() => onRowTap(a)}
                        disabled={busy !== null || confirmed}
                        className={`flex min-h-12 w-full items-center justify-between rounded border px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                          dq
                            ? 'border-red-800 bg-red-950/30 hover:border-red-500'
                            : confirmed
                              ? 'border-zinc-700 bg-zinc-800/40'
                              : done
                                ? 'border-emerald-700 bg-emerald-900/30 hover:border-emerald-500'
                                : inProgress
                                  ? 'border-amber-700 bg-amber-900/20 hover:border-amber-500'
                                  : 'border-zinc-700 hover:border-emerald-500'
                        }`}
                      >
                        <span className="flex items-center gap-2">
                          <span
                            className={
                              dq
                                ? 'text-red-400'
                                : confirmed
                                  ? 'text-emerald-500'
                                  : done
                                    ? 'text-emerald-400'
                                    : inProgress
                                      ? 'text-amber-400'
                                      : 'text-zinc-500'
                            }
                          >
                            {dq ? '❌' : confirmed ? '🔒' : done ? '✓' : inProgress ? '▶' : '○'}
                          </span>
                          <span className="font-medium">{shooterLabel(a)}</span>
                          {a.memberNo && (
                            <span className="text-xs text-zinc-500">{a.memberNo}</span>
                          )}
                        </span>
                        <span className="flex items-center gap-3 text-xs tabular-nums text-zinc-400">
                          {multiLane && a.laneName && (
                            <span className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-300">
                              {a.laneName}
                            </span>
                          )}
                          {multiLane && !a.laneName && (
                            <span className="rounded border border-dashed border-zinc-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-zinc-500">
                              Bahn?
                            </span>
                          )}
                          <span>
                            {a.shotCount} / {g.stage.shotCount}
                          </span>
                          {dq && <span className="text-red-400">DQ</span>}
                          {!dq && confirmed && <span className="text-emerald-500">bestätigt</span>}
                          {!dq && !confirmed && owned && (
                            <span className="text-emerald-400">meine Bahn</span>
                          )}
                          {!dq && !confirmed && otherRef && (
                            <span className="text-amber-400">belegt</span>
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })
      )}

      {/* Kept apart from the start buttons so it isn't hit by accident (UX review REF-15).
          No native confirm: the Badeschluss signature overlay already asks (REF-25).
          Only for a live training: closing needs status 'live' (session_not_live). */}
      {!notStarted && (
        <div className="border-t border-zinc-800 pt-4">
          <button
            onClick={onBadeschluss}
            className="min-h-12 w-full rounded-lg border border-amber-700 bg-amber-900/30 px-4 py-3 text-base font-bold text-amber-200 hover:border-amber-500"
          >
            🛁 Badeschluss — Training beenden
          </button>
        </div>
      )}

      {showUndo && undo && (
        <div role="status" className="fixed inset-x-0 bottom-0 z-40 flex justify-center p-4">
          <div className="flex w-full max-w-md items-center justify-between gap-3 rounded-lg border border-zinc-700 bg-zinc-800 px-4 py-2 text-sm text-zinc-100 shadow-lg">
            <span className="min-w-0 truncate">{undo.label} aufgerufen</span>
            <button
              type="button"
              onClick={() => void uncall(undo.assignmentId)}
              disabled={busy !== null}
              className="min-h-12 shrink-0 rounded px-3 font-bold text-amber-300 underline hover:text-amber-200 disabled:opacity-50"
            >
              Rückgängig
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

interface LanePromptProps {
  assignment: LiveAssignment;
  lanes: LaneRow[];
  onCancel: () => void;
  onPick: (laneId: string) => void;
}

/**
 * Inline lane picker shown when the referee taps a shooter on a multi-
 * lane location that doesn't have a lane yet. Pre-selects the admin's
 * default lane on the participation when present, otherwise the first
 * lane in sort order.
 */
function LanePromptModal({ assignment, lanes, onCancel, onPick }: LanePromptProps) {
  const fallback = assignment.participationLaneId ?? lanes[0]?.id ?? '';
  const [pick, setPick] = useState(fallback);
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-4 sm:items-center">
      <div className="w-full max-w-sm rounded-lg border border-zinc-700 bg-zinc-900 p-4">
        <h3 className="mb-1 text-base font-bold">Bahn wählen</h3>
        <p className="mb-3 text-sm text-zinc-400">
          {(assignment.memberLastName ?? 'Gast') + ', ' + (assignment.memberFirstName ?? '')}
        </p>
        <ul className="mb-4 space-y-1">
          {lanes.map((lane) => (
            <li key={lane.id}>
              <button
                onClick={() => setPick(lane.id)}
                className={`flex min-h-12 w-full items-center justify-between rounded border px-3 py-2 text-left text-sm ${
                  pick === lane.id
                    ? 'border-emerald-500 bg-emerald-900/30'
                    : 'border-zinc-700 hover:border-emerald-600'
                }`}
              >
                <span className="font-medium">{lane.name}</span>
                {assignment.participationLaneId === lane.id && (
                  <span className="text-[10px] uppercase tracking-wider text-emerald-400">
                    Standard
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
        <div className="flex justify-end gap-2">
          <button
            onClick={onCancel}
            className="min-h-12 rounded border border-zinc-700 px-4 py-2 text-sm text-zinc-300"
          >
            Abbrechen
          </button>
          <button
            onClick={() => pick && onPick(pick)}
            disabled={!pick}
            className="min-h-12 rounded bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-600 disabled:opacity-50"
          >
            Übernehmen
          </button>
        </div>
      </div>
    </div>
  );
}
